import React, { ForwardedRef, forwardRef } from 'react';
import Image from 'next/image';
import styled from 'styled-components';

import { ContentContainer, StyledSection } from '@components/atoms';
import { HiddenSeoData, Slider, Article } from '@components/molecules';
import { About } from '@shared/constants/content';
import useDevice from '@hooks/useDevice';

type WhoWeAreProps = {
  content: About[];
};

function AboutCompany(
  { content = [] }: WhoWeAreProps,
  ref: ForwardedRef<HTMLDivElement | null>
) {
  const { isMobile, isTablet, isLaptop, isDesktop } = useDevice();

  const mainContent = content.map(
    ({ title, fullDescription, picture }, index) => {
      const isOdd = index % 2 !== 0;

      return (
        <StyledDiv $flexDirection={isOdd} key={index + title}>
          <ImageWrapper>
            {index === 1 && (
              <StyledIcon
                width={120}
                height={120}
                loading="lazy"
                alt="clean-eco-icon"
                src="/icons/clean-eco.svg"
              />
            )}

            <Wrapper>
              <Image
                fill
                priority
                alt={title}
                src={picture}
                sizes="(max-width: 1440px) 744px, (max-width: 935px) 895px, (max-width: 650px) 610px, (max-width: 375px) 335px"
              />
            </Wrapper>
          </ImageWrapper>

          <Article
            title={title}
            description={fullDescription}
            wrapWidth="38%"
          />
        </StyledDiv>
      );
    }
  );

  return (
    <StyledSection ref={ref}>
      <HiddenSeoData>
        <h2>About company</h2>

        {mainContent}
      </HiddenSeoData>

      <ContentContainer>
        {(isLaptop || isDesktop) && mainContent}

        {(isMobile || isTablet) && <Slider content={content} />}
      </ContentContainer>
    </StyledSection>
  );
}

const StyledDiv = styled.div<{ $flexDirection: boolean }>`
  display: flex;

  justify-content: space-between;
  margin-bottom: 20px;
  flex-direction: ${({ $flexDirection }) =>
    $flexDirection ? 'row-reverse' : 'row'};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  padding-bottom: 56.25%;
  border-radius: 16px;
  overflow: hidden;
`;

const StyledIcon = styled(Image)`
  position: absolute;
  z-index: 1;
  right: -50px;
  top: -50px;
`;

const ImageWrapper = styled.div`
  width: 60%;
  position: relative;
`;

export default forwardRef<HTMLDivElement | null, WhoWeAreProps>(AboutCompany);
